import React, { useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Settings, Edit2, School, Key, CheckCircle2, AlertCircle, Users } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import { type Classroom, type TeacherAccount, verifyPassword, hashPassword, isValidPassword } from '../../../accounts';
interface SettingsPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  onNavigate?: (route: string) => void;
}
export function SettingsPage({
  activeAccount,
  classrooms = [],
  onNavigate
}: SettingsPageProps) {
  // Singleton store — all consumers share the same state (Zustand refactor s81)
  const {
    accounts,
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updateTeacherDetails,
    updatePassword
  } = useAppStore();
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount?.displayName || '');
  const [editEmail, setEditEmail] = useState(activeAccount?.email || '');
  const [editSchool, setEditSchool] = useState(activeAccount?.schoolName || '');
  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    loadDemoAccounts: () => {},
    clearActiveAccount
  });
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    if (editSchool.trim() !== (activeAccount.schoolName || '')) {
      updateTeacherDetails(activeAccount.id, {
        schoolName: editSchool.trim()
      });
    }
    setIsEditingProfile(false);
  };
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Vul alle velden in.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('De nieuwe wachtwoorden komen niet overeen.');
      return;
    }
    if (!isValidPassword(newPassword, 'teacher')) {
      setPasswordError('Het nieuwe wachtwoord moet minimaal 6 tekens lang zijn.');
      return;
    }
    if (!activeAccount.passwordHash) {
      setPasswordError('Dit account heeft geen wachtwoord ingesteld.');
      return;
    }
    const isCorrect = await verifyPassword(currentPassword, activeAccount.passwordHash);
    if (!isCorrect) {
      setPasswordError('Het huidige wachtwoord is onjuist.');
      return;
    }
    const newHash = await hashPassword(newPassword);
    updatePassword(activeAccount.id, newHash);
    setPasswordSuccess('Je wachtwoord is succesvol gewijzigd.');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => {
      setIsChangingPassword(false);
      setPasswordSuccess('');
    }, 3000);
  };
  return <PageLayout title="Instellingen" description="Beheer je docentaccount en voorkeuren" data-id="element-6808">
      <div className="max-w-3xl mx-auto space-y-6" data-id="element-6809">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Beheer je persoonlijke gegevens" icon={Settings} accentColor="bg-violet-100 text-violet-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount?.displayName || '');
        setEditEmail(activeAccount?.email || '');
        setEditSchool(activeAccount?.schoolName || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-6811">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-6812" /> Bewerk
              </Button> : null} data-id="element-6810">
          <div className="space-y-6" data-id="element-6813">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-6814">
                <div data-id="element-6815">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-6816">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-6817" />
                </div>
                <div data-id="element-6818">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-6819">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-6820" />
                </div>
                <div className="sm:col-span-2" data-id="element-6821">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-6822">
                    School
                  </label>
                  <Input variant="admin" value={editSchool} onChange={e => setEditSchool(e.target.value)} placeholder="Naam van je school" data-id="element-6823" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-6824">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-6825">
                    Annuleren
                  </Button>
                  <button onClick={handleSaveProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-6826">
                    Opslaan
                  </button>
                </div>
              </div> : <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-6827">
                  <div data-id="element-6828">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-6829">
                      Naam
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-6830">
                      {activeAccount?.displayName}
                    </div>
                  </div>
                  <div data-id="element-6831">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-6832">
                      E-mailadres
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-6833">
                      {activeAccount?.email || <span className="text-slate-400 italic" data-id="element-6834">
                          Niet ingesteld
                        </span>}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-6835">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 max-w-sm" data-id="element-6836">
                    <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500 shadow-sm" data-id="element-6837">
                      <School className="w-5 h-5" data-id="element-6838" />
                    </div>
                    <div data-id="element-6839">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-6840">
                        School
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-6841">
                        {activeAccount?.schoolName || 'Niet opgegeven'}
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-violet-100 text-violet-600" action={!isChangingPassword ? <Button onClick={() => setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-6843">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-6844" /> Wijzig
              </Button> : null} data-id="element-6842">
          {isChangingPassword ? <form onSubmit={handleChangePassword} className="space-y-4" data-id="element-6845">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6846">
                <div className="sm:col-span-2" data-id="element-6847">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-6848">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-6849" />
                </div>
                <div data-id="element-6850">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-6851">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-6852" />
                </div>
                <div data-id="element-6853">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-6854">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-6855" />
                </div>
              </div>

              {passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-6856">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-6857" />
                  <p data-id="element-6858">{passwordError}</p>
                </div>}

              {passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-6859">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-6860" />
                  <p data-id="element-6861">{passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-6862">
                <Button type="button" onClick={() => {
              setIsChangingPassword(false);
              setPasswordError('');
              setPasswordSuccess('');
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
            }} variant="secondary" data-id="element-6863">
                  Annuleren
                </Button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-6864">
                  Wachtwoord Wijzigen
                </button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-6865">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Klassen Overzicht */}
        <SettingsSection title="Klassen Overzicht" description="Je actieve klassen" icon={Users} accentColor="bg-blue-100 text-blue-600" action={<Button onClick={() => onNavigate?.('management')} variant="ghost" size="sm" data-id="element-6867">
              Beheer klassen
            </Button>} data-id="element-6866">
          {classrooms.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6868">
              {classrooms.map(c => <div key={c.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-6869">
                  <div className="font-bold text-slate-900" data-id="element-6870">{c.name}</div>
                  <div className="text-sm text-slate-500 mt-1" data-id="element-6871">
                    Groep {c.groupLevel} • {c.learnerIds.length} leerlingen
                  </div>
                </div>)}
            </div> : <div className="text-sm text-slate-500" data-id="element-6872">
              Je hebt nog geen klassen.
            </div>}
        </SettingsSection>
      </div>
    </PageLayout>;
}